import React, { useCallback, useMemo, useState, useEffect } from 'react';
import classnames from 'classnames';
import BillingInputComponent from '../../template-blocks/billing-input/billing-input-component';
import billingInputTypes from '../../template-blocks/billing-input/billing-input-types';
import FilesDropzoneComponent from '../../template-blocks/files-dropzone/files-dropzone-component';
import { v4 as uuidv4 } from 'uuid';

import { mondayNotice } from '../../../services/monday-service';
import { noticeTypes, S3_BUCKET_PREFIX } from '../../../constants';
import { filesAPI } from '../../../api';
import { getFileAsArrayBuffer } from '../../../services/file-service';
import { uploadFileToAWSS3 } from '../../../services/aws-service';
import EditableInput from '../../template-blocks/editable-input/editable-input';
import TipseenTooltip from '../../tipseen-tooltip/tipseen-tooltip-component';
import { imageDataFromUrl } from '../../../services/images-service';
import { EDIT_DOCUMENT_HEADER } from '../../../constants/tipseens';
import { useTipseens } from '../../../contexts/tipseen-context';

import './quote-description-component.scss';

const IMAGE_FILE_TYPES = ['image/jpeg', 'image/png', 'image/bmp'];
const MAX_IMAGE_SIZE = 1048576; // 1MB
const DEFAULT_TITLE = 'Quote';

export default function QuoteDescriptionComponent({
  image,
  title,
  description,
  data,
  onTemplateDataChange,
  id,
  previewMode,
  sessionToken,
  boardId,
  accountId,
}) {
  const [imageData, setImageData] = useState('');
  const { isTipseenSynced, syncTipseen } = useTipseens();

  const imageUrl = useMemo(() => data && image && data[image.id], [data, image]);

  const quoteTitle = useMemo(() => {
    const titleFromData = data[title.id];
    if (titleFromData || titleFromData === '') {
      return titleFromData;
    }

    return DEFAULT_TITLE;
  }, [data, title]);

  const shouldShowEditDocumentHeaderTip = useMemo(() => {
    return !isTipseenSynced(EDIT_DOCUMENT_HEADER);
  }, [isTipseenSynced]);

  const tipSeenProps = useMemo(() => {
    if (!shouldShowEditDocumentHeaderTip) return { open: false };

    return {
      position: 'right',
      open: true,
      title: 'Name your document',
      description: 'You can edit your document’s title to be a quote, invoice or anything you’d like.',
      tipseen: EDIT_DOCUMENT_HEADER,
      syncTipseen,
    };
  }, [shouldShowEditDocumentHeaderTip, syncTipseen]);

  useEffect(() => {
    let isSubscribed = true;
    const getImageData = async () => {
      const data = await imageDataFromUrl(imageUrl);
      if (!isSubscribed) return;
      setImageData(data);
    };

    imageUrl && getImageData();

    return () => (isSubscribed = false);
  }, [imageUrl]);

  const onCompanyLogoDropped = useCallback(
    async (accetpedFiles, rejectedFiles) => {
      if (rejectedFiles && rejectedFiles.length > 0) {
        mondayNotice({ type: noticeTypes.ERROR, message: rejectedFiles[0].errors[0].message });
        return;
      }

      if (!accetpedFiles || accetpedFiles.length !== 1) {
        mondayNotice({ type: noticeTypes.ERROR, message: 'Only one file allowed' });
        return;
      }

      const file = accetpedFiles[0];
      const fileKey = `${accountId}/company-logos/${boardId}/${uuidv4()}/${file.name}`;

      try {
        const { url } = await filesAPI.getAWSPresignedUploadUrl(sessionToken, fileKey, file.type);
        const fileArrayBuffer = await getFileAsArrayBuffer(file);
        const response = await uploadFileToAWSS3(url, { fileContent: fileArrayBuffer, contentType: file.type });
        if (!response.ok) {
          throw new Error();
        }

        const imageUrl = `${S3_BUCKET_PREFIX}/${fileKey}`;
        onTemplateDataChange(id, { ...data, [image.id]: imageUrl });
      } catch (e) {
        mondayNotice({ type: noticeTypes.ERROR, message: 'Error while uploading file' });
      }
    },
    [boardId, accountId, sessionToken, id, data, image.id, onTemplateDataChange]
  );

  const onBillingInputChange = useCallback(
    (inputId, inputData) => {
      onTemplateDataChange(id, { ...data, [inputId]: inputData });
    },
    [id, onTemplateDataChange, data]
  );

  const renderCompanyLogoContent = () => {
    let content = previewMode ? '' : 'Add company Logo';

    if (imageData) {
      content = <img src={imageData} alt="Company Logo" />;
    }

    return <div className="content-wrapper">{content}</div>;
  };

  const handleTitleChange = useCallback(
    (newTitle) => {
      onTemplateDataChange(id, { ...data, [title.id]: newTitle });
    },
    [id, title, onTemplateDataChange, data]
  );

  const renderTitle = () => {
    if (previewMode) {
      return <div className="quote-title">{quoteTitle}</div>;
    }

    return (
      <div className="quote-title">
        <TipseenTooltip {...tipSeenProps}>
          <EditableInput value={quoteTitle} onBlur={handleTitleChange} />
        </TipseenTooltip>
      </div>
    );
  };

  return (
    <div className={classnames('quote-description-component', 'pdf-row', { preview: previewMode })}>
      <div className="quote-description">
        <div className="quote-title-input-wrapper">{renderTitle()}</div>
        <div className="quote-description-input-wrapper">
          <BillingInputComponent
            id={description.id}
            type={billingInputTypes.text}
            previewMode={previewMode}
            withTitle={false}
            placeholder="Add description"
            onChange={onBillingInputChange}
            value={data[description.id]?.data || ''}
          />
        </div>
      </div>
      <div className="quote-image-wrapper">
        <FilesDropzoneComponent
          maxFiles={1}
          placeholder="Add company Logo"
          acceptFileTypes={IMAGE_FILE_TYPES}
          onFilesDrop={onCompanyLogoDropped}
          disabled={previewMode}
          multiple={false}
          maxSize={MAX_IMAGE_SIZE}
        >
          {renderCompanyLogoContent()}
        </FilesDropzoneComponent>
      </div>
    </div>
  );
}
